import { useEffect, useState, useCallback, useRef } from "react";

export default function PastSubmissionsTable({ idToken, triggerRefresh }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareRow, setShareRow] = useState(null);
  const [emailsText, setEmailsText] = useState("");
  const [shareLoading, setShareLoading] = useState(false);

  const [existingShares, setExistingShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);

  // ---- Toast (stable timeout via useRef) ----
  const DEFAULT_TOAST_DURATION = 20000; // 20s default
  const [popup, setPopup] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((message, type = "info", title = "", duration = DEFAULT_TOAST_DURATION) => {
    setPopup({ message, type, title });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setPopup(null), duration);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);
  // ------------------------------------------

  const API_BASE = process.env.REACT_APP_API_BASE || "";
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
  const orDash = (v) => (v && String(v).trim() ? v : "—");
  const safeJson = async (res) => { try { return await res.json(); } catch { return {}; } };


  // function openShareModal(row) {
  //   setShareRow(row);
  //   setEmailsText("");
  //   setShareOpen(true);
  // }

  async function openShareModal(row) {
    setShareRow(row);
    setEmailsText("");
    setShareOpen(true);
    setExistingShares([]);
    setSharesLoading(true);
    
    try {
      const res = await fetch(
        `${API_BASE}/submissions/${encodeURIComponent(row.TaskId)}/shares`,
        {
          headers: {
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
        }
      );
    
      const payload = await safeJson(res);
      if (!res.ok) throw new Error(payload?.error || "Failed to load share list");
    
      setExistingShares(payload.items || []);
    } catch (err) {
      showToast(String(err?.message || err), "error");
    } finally {
      setSharesLoading(false);
    }
  }
    
  async function submitShare() {
    if (!shareRow) return;
    
    const emails = emailsText
      .split(/[,\n;]/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    
    if (!emails.length) {
      showToast("Enter at least one email address", "error");
      return;
    }
    
    try {
      setShareLoading(true);
    
      const res = await fetch(
        `${API_BASE}/submissions/${encodeURIComponent(shareRow.TaskId)}/share`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify({ emails }),
        }
      );
    
      const payload = await safeJson(res);
      if (!res.ok) throw new Error(payload?.error || "Failed to share report");
    
      const sharedCount = payload?.shared?.length || 0;
      const notFoundCount = payload?.notFound?.length || 0;
    
      showToast(
        `Shared with ${sharedCount} user(s)` +
          (notFoundCount ? `, ${notFoundCount} email(s) not found` : ""),
        "success"
      );
    
      setShareOpen(false);
      setShareRow(null);
      setEmailsText("");
    } catch (err) {
      showToast(String(err?.message || err), "error");
    } finally {
      setShareLoading(false);
    }
  }

  const fetchPastSubmissions = useCallback(async () => {
    if (!idToken) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/jobs?limit=25`, {
        headers: {
          "Content-Type": "application/json",
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
      });
      if (!res.ok) {
        const err = await safeJson(res);
        showToast(err?.error || "Failed to load submissions", "error");
        return;
      }
      const data = await res.json();
      // setSubmissions(data.items || []);
      const rows = (data.items || []).map((r) => ({
        ...r,
        // normalize whatever backend returns into the boolean the checkbox uses
        IsPublic: r.IsPublic === true || r.IsPublicStr === "true" || r.isPublic === true,
      }));
      setSubmissions(rows);
    } catch {
      showToast("Network error while loading submissions", "error");
    } finally {
      setLoading(false);
    }
  }, [API_BASE, idToken, showToast]);

  useEffect(() => {
    if (idToken) fetchPastSubmissions();
  }, [fetchPastSubmissions, idToken, triggerRefresh]);

  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* Toast */}
      {popup && (
        <div
          className={`fixed top-5 right-5 z-50 rounded shadow-lg border px-6 py-4 text-sm
            ${popup.type === "success" ? "bg-green-100 border-green-400 text-green-800"
              : popup.type === "error" ? "bg-red-100 border-red-400 text-red-800"
              : "bg-yellow-100 border-yellow-300 text-yellow-800"}`}
        >
          {popup.title && <strong className="block font-medium mb-0.5">{popup.title}</strong>}
          <span>{popup.message}</span>
        </div>
      )}

      {/* Header with Refresh */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Recent Submissions</h2>
        <button
          onClick={fetchPastSubmissions}
          disabled={loading || !idToken}
          className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm disabled:opacity-60"
          title={!idToken ? "Waiting for sign-in…" : undefined}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Card container */}
      <div className="border rounded-xl min-h-[200px] items-center ">
        <div className="p-0">
          {loading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : submissions.length === 0 ? (
            <p className="text-sm text-gray-500">No submissions found.</p>
          ) : (
            <div className="overflow-x-auto max-h-[240px] overflow-y-auto rounded-lg">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100 sticky top-0">
                  <tr className="whitespace-nowrap">
                    <th className="px-3 py-2">File Name</th>
                    <th className="px-3 py-2">Stem</th>
                    <th className="px-3 py-2">Pull date</th>
                    <th className="px-3 py-2">Platform</th>
                    <th className="px-3 py-2">Schema</th>
                    <th className="px-3 py-2">Account Type</th>
                    <th className="px-3 py-2">Submitted at</th>
                    <th className="px-3 py-2 text-center">Report</th>
                    <th className="px-3 py-2 text-center">Public</th>
                    <th className="px-3 py-2 text-center">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.TaskId} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 max-w-[220px]">
                        <span title={s.UserFileName} className="block truncate">
                          {orDash(s.UserFileName)}
                        </span>
                      </td>
                      <td className="px-3 py-2">{orDash(s.Stem)}</td>
                      <td className="px-3 py-2">{orDash(s.PullDate)}</td>
                      <td className="px-3 py-2">{orDash(s.Platform)}</td>
                      <td className="px-3 py-2 max-w-[180px]">
                        <span title={s.Schema} className="block truncate">
                          {orDash(s.Schema)}
                        </span>
                      </td>
                      <td className="px-3 py-2">{orDash(s.AccountType)}</td>
                      <td className="px-3 py-2">{fmt(s.SubmissionTime)}</td>

                      <td className="px-3 py-2 text-center">
                        <button
                          disabled={s._loading}
                          onClick={async () => {
                            setSubmissions((rows) =>
                              rows.map((r) =>
                                r.TaskId === s.TaskId ? { ...r, _loading: true } : r
                              )
                            );
                            
                            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
                            setPopup(null);
                            
                            try {
                              const res = await fetch(
                                `${API_BASE}/get-links/${encodeURIComponent(
                                  s.TaskId
                                )}/signed-url?jobId=${encodeURIComponent(s.JobId || "")}`,
                                {
                                  headers: {
                                    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
                                  },
                                }
                              );
                            
                              const status = res.status;
                              let payload = {};
                              try {
                                payload = await res.json();
                              } catch {
                                payload = {};
                              }
                            
                              if (status === 401) {
                                showToast("Your session expired. Please sign in again.", "error");
                              } else if (status === 403) {
                                showToast("You don’t have access to this report (403).", "error");
                              } else if (status === 400) {
                                showToast("Invalid request (400). Please refresh and try again.", "error");
                              } else if (status >= 500) {
                                showToast("Server error while preparing your report. Please try again.", "error");
                              } else {
                                if (status === 200 && payload && payload.ok === false) {
                                  const code = payload.code;
                                  const state = payload.state;
                            
                                  if (code === "report_not_ready" && state === "running") {
                                    showToast("Your job is still running. The report isn’t ready yet.", "info");
                                  } else if (code === "report_not_ready" && state === "stopped_success") {
                                    showToast("Job completed — preparing the report. Try again shortly.", "success");
                                  } else if (code === "report_not_found") {
                                    showToast(
                                      "The job has stopped. Ensure correct platform/schema and dataset is selected, or contact the administrator.",
                                      "error",
                                      "Report not found"
                                    );
                                  } else {
                                    showToast("Report not available yet.", "info");
                                  }
                            
                                  setSubmissions((rows) =>
                                    rows.map((r) =>
                                      r.TaskId === s.TaskId ? { ...r, _loading: false } : r
                                    )
                                  );
                                  return;
                                }
                            
                                if (!res.ok) {
                                  const state = payload?.state;
                                  const reason = payload?.reason;
                            
                                  if (status === 425 && state === "running") {
                                    showToast("Your job is still running. The report isn’t ready yet.", "info");
                                  } else if (status === 409 && state === "stopped_success") {
                                    showToast("Job completed — preparing the report. Try again shortly.", "success");
                                  } else if (
                                    status === 404 &&
                                    (state === "stopped_failure" || state === "stopped_manual" || state === "unknown")
                                  ) {
                                    showToast(
                                      "The job has stopped. Ensure correct platform/schema and dataset is selected, or contact the administrator.",
                                      "error",
                                      "Report not found"
                                    );
                                  } else {
                                    showToast(`Something went wrong${reason ? `: ${reason}` : ""}`, "error");
                                  }
                            
                                  setSubmissions((rows) =>
                                    rows.map((r) =>
                                      r.TaskId === s.TaskId ? { ...r, _loading: false } : r
                                    )
                                  );
                                  return;
                                }
                            
                                if (payload?.url) {
                                  if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
                                  setPopup(null);
                                  setSubmissions((rows) =>
                                    rows.map((r) =>
                                      r.TaskId === s.TaskId ? { ...r, _loading: false, _url: payload.url } : r
                                    )
                                  );
                                  window.location.assign(payload.url);
                                  return;
                                }
                            
                                showToast("No report URL received. Please try again.", "error");
                              }
                            
                              setSubmissions((rows) =>
                                rows.map((r) =>
                                  r.TaskId === s.TaskId ? { ...r, _loading: false } : r
                                )
                              );
                            } catch {
                              setSubmissions((rows) =>
                                rows.map((r) =>
                                  r.TaskId === s.TaskId ? { ...r, _loading: false } : r
                                )
                              );
                              showToast("Network or unexpected error. Please try again.", "error");
                            }
                          }}
                          className="text-blue-600 hover:underline disabled:text-gray-400"
                        >
                          {s._loading ? "Preparing…" : "link"}
                        </button>
                      </td>

                    
                      <td className="px-3 py-2 text-center">
                        <label className="inline-flex items-center justify-center cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-orange-600"
                            checked={!!s.IsPublic}
                            disabled={!!s._publicLoading}
                            onChange={async (e) => {
                              const next = e.target.checked;
                        
                                // optimistic UI
                              setSubmissions((rows) =>
                                rows.map((r) =>
                                  r.TaskId === s.TaskId ? { ...r, IsPublic: next, _publicLoading: true } : r
                                )
                              );
                        
                              try {
                                const res = await fetch(
                                  `${API_BASE}/submissions/${encodeURIComponent(s.TaskId)}/public`,
                                  {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
                                    },
                                      body: JSON.stringify({ isPublic: next }),
                                  }
                                );
                        
                                const payload = await safeJson(res);
                                if (!res.ok) throw new Error(payload?.error || "Failed to update public status");
                        
                                showToast(next ? "Made public" : "Made private", "success");
                              } catch (err) {
                                  // rollback if failed
                                setSubmissions((rows) =>
                                  rows.map((r) =>
                                    r.TaskId === s.TaskId ? { ...r, IsPublic: !next } : r
                                  )
                                );
                                showToast(String(err?.message || err), "error");
                              } finally {
                                setSubmissions((rows) =>
                                  rows.map((r) =>
                                    r.TaskId === s.TaskId ? { ...r, _publicLoading: false } : r
                                  )
                                );
                              }
                            }}
                          />
                          {/* <span className="text-xs text-slate-600">
                            {s.IsPublic ? "Public" : "Private"}
                          </span> */}
                        </label>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => openShareModal(s)}
                          className="text-orange-700 hover:underline disabled:text-gray-400"
                          disabled={!!s._publicLoading || !!s._loading}
                        >
                          Share
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {shareOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
            <h3 className="mb-3 text-lg font-semibold">Share report</h3>
        
            <p className="mb-2 text-sm text-slate-600">
              Enter one or more email addresses separated by commas or new lines.
            </p>
        
            <textarea
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              className="w-full rounded-lg border p-3 text-sm"
              rows={6}
              placeholder="user1@example.com, user2@example.com"
            />

            <div className="mt-4">
              <h4 className="mb-2 text-sm font-semibold">Currently shared with</h4>
            
              {sharesLoading ? (
                <p className="text-sm text-slate-500">Loading recipients…</p>
              ) : existingShares.length === 0 ? (
                <p className="text-sm text-slate-500">Not shared with anyone yet.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {existingShares.map((r) => (
                    <div
                      key={`${r.sharedWithUserId}-${r.sharedAt || ""}`}
                      className="rounded border px-3 py-2 text-sm"
                    >
                      <div className="font-medium">
                        {r.sharedWithEmail || r.sharedWithUserId}
                      </div>
                      {r.sharedAt && (
                        <div className="text-xs text-slate-500">
                          Shared {new Date(r.sharedAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
        
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShareOpen(false);
                  setShareRow(null);
                  setEmailsText("");
                }}
                className="rounded-lg border px-4 py-2 text-sm"
                disabled={shareLoading}
              >
                Cancel
              </button>
              <button
                onClick={submitShare}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                disabled={shareLoading}
              >
                {shareLoading ? "Sharing…" : "Share"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
